import React, { useEffect, useState } from 'react';
import { Camera, Shield, Key, Bell, Globe, Save } from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";
import { updateProfile, changePassword } from "./adminSlice/adminSlice";

function AdminProfile() {
  const [activeTab, setActiveTab] = useState('profile');
  const dispatch = useDispatch();
  const { admin } = useSelector((state) => state.admin);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    bio: '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
  });

  // ✅ AUTO FILL FROM REDUX
  useEffect(() => {
    if (admin) {
      setProfile({
        name: admin.name || '',
        email: admin.email || '',
        phone: admin.phone || '',
        role: admin.role || '',
        bio: admin.bio || '',
      });

      setPreview(admin.profilePic || "");
    }
  }, [admin]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setProfilePicFile(file);
    setPreview(URL.createObjectURL(file)); // 👀 preview instantly
  };

  // ✅ UPDATE PROFILE
  const handleUpdate = async () => {
    setMessage("");
    setErrorMsg("");
    const formData = new FormData();

    formData.append("id", admin._id);
    formData.append("name", profile.name);
    formData.append("email", profile.email);
    formData.append("phone", profile.phone);
    formData.append("bio", profile.bio);

    if (profilePicFile) {
      formData.append("profilePic", profilePicFile);
    }

    const res = await dispatch(updateProfile(formData));

    if (res.meta.requestStatus === "fulfilled") {
      setMessage("Changes saved successfully.");
    } else {
      setErrorMsg(res.payload || "Something went wrong.");
    }
  };

  // ✅ CHANGE PASSWORD
  const handlePassword = async () => {
    setMessage("");
    setErrorMsg("");
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      alert("Please fill all fields");
      return;
    }

    const res = await dispatch(changePassword({
      id: admin._id,
      ...passwordData
    }));

    if (res.meta.requestStatus === "fulfilled") {
      setMessage("Password updated successfully.");

      setPasswordData({
        currentPassword: "",
        newPassword: "",
      });
    } else {
      setErrorMsg(res.payload || "Failed to update password.");
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <Globe size={15} /> },
    { id: 'security', label: 'Security', icon: <Shield size={15} /> },
  ];

  return (
    <div className="page-container">
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Admin Profile</h1>
          <p className="page-subtitle">Manage your account settings and preferences.</p>
        </div>
      </div>

      <div className="profile-layout">

        <div className="profile-left">

          <div className="card" style={{ textAlign: 'center', padding: 28 }}>
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>

              <img
                src={
                  preview ||
                  (admin?.profilePic
                    ? `${admin.profilePic}?t=${Date.now()}`
                    : "/images/profile.jpg")
                }
                alt="profile"
                onError={(e) => {
                  e.target.src = "/images/profile.jpg";
                }}
                style={{
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  objectFit: "cover",
                  boxShadow: "0 4px 20px rgba(232,0,29,0.35)"
                }}
              />

              <label style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: 28,
                height: 28,
                background: 'var(--accent-gradient)',
                border: '2px solid var(--bg-card)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                cursor: 'pointer',
              }}>
                <Camera size={12} />

                {/* hidden input */}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
              </label>
            </div>

            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 18, color: 'var(--text-primary)' }}>
              {profile.name || "Vegas Karts"}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{profile.email || "admin@vegas.com"}</div>
            <span className="badge badge-danger" style={{ fontSize: 12 }}>{profile.role || "Super Admin"}</span>
          </div>

          {/* Quick-access card — hidden on mobile (tabs above are enough) */}
          <div className="card profile-quick-access" style={{ marginTop: 16, padding: 16 }}>
            <div style={{
              fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13,
              color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12,
            }}>Quick Access</div>
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '9px 10px', borderRadius: 8, border: 'none',
                  textAlign: 'left', cursor: 'pointer',
                  background: activeTab === t.id ? 'rgba(232,0,29,0.1)' : 'transparent',
                  color: activeTab === t.id ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: 14,
                  transition: 'all 0.2s', marginBottom: 2,
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="profile-right">

          {/* Tab bar */}
          <div className="profile-tabs">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`profile-tab-btn${activeTab === t.id ? ' active' : ''}`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* ── Profile tab ── */}
          {activeTab === 'profile' && (
            <div className="card animate-in">
              <h3 className="font-heading" style={{ fontSize: 16, marginBottom: 20 }}>Personal Information</h3>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input className="form-control" value={profile.name || "Vegas Karts"}
                    onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} />
                </div>
                {/* <div className="form-group">
                  <label className="form-label">Role</label>
                  <input className="form-control" value={profile.role} readOnly style={{ opacity: 0.6 }} />
                </div> */}
                {/* </div>
              <div className="grid-2"> */}
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input className="form-control" type="email" value={profile.email || "admin@vegas.com"}
                    onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input className="form-control" value={profile.phone || "1234567895"}
                    onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Bio</label>
                <textarea className="form-control" value={profile.bio || "Managing Vegas Karts admin panel operations."}
                  onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
                  style={{ minHeight: 80 }} />
              </div>
              <button className="btn-primary" onClick={handleUpdate}><Save size={15} /> Save Changes</button>
              {message && (
                <div style={{ color: "green", marginTop: 10 }}>
                  {message}
                </div>
              )}

              {errorMsg && (
                <div style={{ color: "red", marginTop: 10 }}>
                  {errorMsg}
                </div>
              )}
            </div>
          )}

          {/* ── Security tab ── */}
          {activeTab === 'security' && (
            <div className="card animate-in">
              <h3 className="font-heading" style={{ fontSize: 16, marginBottom: 20 }}>Change Password</h3>
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input className="form-control" type="password" placeholder="••••••••" value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  } />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input className="form-control" type="password" placeholder="••••••••" value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  } />
              </div>
              <button className="btn-primary" onClick={handlePassword} style={{ marginBottom: 28 }}><Key size={15} /> Update Password</button>
              {message && activeTab === "security" && (
                <div style={{ color: "green", marginTop: 10 }}>
                  {message}
                </div>
              )}

              {errorMsg && activeTab === "security" && (
                <div style={{ color: "red", marginTop: 10 }}>
                  {errorMsg}
                </div>
              )}

            </div>
          )}
        </div>
      </div>

      {/* ── Scoped responsive styles ── */}
      <style>{`
        /* Two-column layout */
        .profile-layout {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 24px;
          align-items: start;
        }

        /* Tab bar */
        .profile-tabs {
          display: flex;
          gap: 2px;
          margin-bottom: 20px;
          background: var(--bg-card);
          padding: 4px;
          border-radius: 10px;
          width: fit-content;
          max-width: 100%;
          border: 1px solid var(--border);
          flex-wrap: wrap;
        }
        .profile-tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          background: transparent;
          color: var(--text-muted);
          font-family: 'JetBrains Mono', monospace;
          font-weight: 600;
          font-size: 13px;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .profile-tab-btn.active {
          background: var(--accent-gradient);
          color: #fff;
        }

        /* ── Tablet (≤900px): stack columns ── */
        @media (max-width: 900px) {
          .profile-layout {
            grid-template-columns: 1fr;
          }
          /* Left column: horizontal card on tablet */
          .profile-left .card:first-child {
            display: flex;
            align-items: center;
            gap: 0;
            text-align: left;
            padding: 20px;
            flex-direction:column;
          }
          .profile-left .card:first-child > div:first-child {
            flex-shrink: 0;
          }
        }

        /* ── Mobile (≤600px) ── */
        @media (max-width: 600px) {
          .profile-layout { gap: 16px; }

          /* Avatar card: vertical again on small phones */
          .profile-left .card:first-child {
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 20px 16px;
            gap:0;
          }

          /* Hide sidebar quick-access on mobile — tabs are enough */
          .profile-quick-access { display: none; }

          .profile-tabs {
            width: 100%;
            justify-content: stretch;
          }
          .profile-tab-btn {
            flex: 1;
            justify-content: center;
            padding: 8px 8px;
            font-size: 12px;
            gap: 4px;
          }
        }

        /* ── Very small (≤380px) ── */
        @media (max-width: 380px) {
          .profile-tab-btn span { display: none; } /* icon only */
          .profile-tab-btn { padding: 8px; }
        }
      `}</style>
    </div>
  );
}

export default AdminProfile;